import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Select from 'react-select';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from './ConfirmModal';
import ErrorModal from './ErrorModal';
import { isProjectBudgetLocked, BUDGET_LOCKED_MESSAGE } from '../utils/projectBudget';

const parseHHMMToDecimalHours = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
};

// "Business Development" is appended only for users with the Projektleder
// role (see admin panel) — everyone else never sees it in the dropdown.
function getZrmTypeOptions(canUseBD) {
  return [
    { value: 'Udvikling', label: 'Udvikling' },
    { value: 'Møde', label: 'Møde' },
    { value: 'Support', label: 'Support' },
    ...(canUseBD ? [{ value: 'Business Development', label: 'Business Development' }] : []),
    { value: 'Workshop', label: 'Workshop' },
  ];
}

export default function SeriesModal({
  isOpen,
  onClose,
  onCreateSeries,
  templateEvent,
  projects,
  canUseBD,
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [projectId, setProjectId] = useState('');
  const [showBudgetLocked, setShowBudgetLocked] = useState(false);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEnhancingNotes, setIsEnhancingNotes] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [entryType, setEntryType] = useState('Udvikling');
  // Keep showing "Business Development" if the series is templated off an
  // existing BD event, even for a user without BD access — otherwise the
  // dropdown renders blank and submitting could silently change the type.
  const zrmTypeOptions = getZrmTypeOptions(canUseBD || entryType === 'Business Development');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen && templateEvent) {
      // Pre-fill with template event data
      setStartDate(moment().format('YYYY-MM-DD')); // Start from today
      setEndDate(moment().add(1, 'month').format('YYYY-MM-DD')); // Default 1 month

      // Extract start and end times from template event
      setStartTime(moment(templateEvent.start).format('HH:mm'));
      setEndTime(moment(templateEvent.end).format('HH:mm'));

      setProjectId(templateEvent.projectId || '');
      // Set default notes if coming from header (empty template)
      setNotes('');
      setSkipWeekends(true);
      setEntryType(templateEvent.type || 'Udvikling');
    } else if (!isOpen) {
      // Reset form when closed
      resetForm();
    }
  }, [isOpen, templateEvent]);

  // ESC key listener (same as EventFormModal)
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, hasChanges]);

  // Track changes in form fields (same as EventFormModal)
  useEffect(() => {
    if (templateEvent && isOpen) {
      setHasChanges(
        startDate !== moment().format('YYYY-MM-DD') ||
        endDate !== moment().add(1, 'month').format('YYYY-MM-DD') ||
        startTime !== moment(templateEvent.start).format('HH:mm') ||
        endTime !== moment(templateEvent.end).format('HH:mm') ||
        projectId !== (templateEvent.projectId || '') ||
        notes !== '' ||
        !skipWeekends // Default is true, so any change from true is a change
      );
    }
  }, [startDate, endDate, startTime, endTime, projectId, notes, skipWeekends, templateEvent, isOpen]);

  const resetForm = () => {
    setStartDate('');
    setEndDate('');
    setStartTime('');
    setEndTime('');
    setSkipWeekends(true);
    setProjectId('');
    setNotes('');
    setErrors({});
    setHasSubmitted(false);
    setIsCreating(false);
    setProgress({ current: 0, total: 0 });
    setHasChanges(false);
    setShowDiscardModal(false);
    setEntryType('Udvikling');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!startDate) newErrors.startDate = "Please select a start date";
    if (!endDate) newErrors.endDate = "Please select an end date";
    if (!startTime) newErrors.startTime = "Please enter a start time";
    if (!endTime) newErrors.endTime = "Please enter an end time";
    if (!projectId) newErrors.projectId = "Please select a project";
    if (!entryType) newErrors.entryType = "Please select a type";
    if (!notes.trim()) newErrors.notes = "Please enter notes";
    if (notes.trim().length < 25) newErrors.notes = "Notes must be at least 25 characters";

    if (startDate && endDate) {
      if (moment(endDate).isBefore(moment(startDate))) {
        newErrors.endDate = "End date must be after start date";
      } else if (startTime && endTime) {
        const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (timePattern.test(startTime) && timePattern.test(endTime)) {
          const hoursPerDay = parseHHMMToDecimalHours(endTime) - parseHHMMToDecimalHours(startTime);
          if (hoursPerDay > 0) {
            const dates = [];
            let cur = moment(startDate);
            const end = moment(endDate);
            while (cur.isSameOrBefore(end)) {
              if (!skipWeekends || (cur.day() !== 0 && cur.day() !== 6)) dates.push(cur);
              cur = cur.clone().add(1, 'day');
            }
            const totalHours = dates.length * hoursPerDay;
            if (totalHours > 168) {
              newErrors.endDate = `Total hours (${totalHours.toFixed(1)}h) exceeds 168h limit. Shorten the date range or reduce daily hours.`;
            }
          }
        }
      }
    }

    // Validate time format and logic
    if (startTime && endTime) {
      const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timePattern.test(startTime)) {
        newErrors.startTime = "Invalid time format";
      }
      if (!timePattern.test(endTime)) {
        newErrors.endTime = "Invalid time format";
      }
      
      if (timePattern.test(startTime) && timePattern.test(endTime)) {
        const startHours = parseHHMMToDecimalHours(startTime);
        const endHours = parseHHMMToDecimalHours(endTime);
        const duration = endHours - startHours;
        
        if (duration <= 0) {
          newErrors.endTime = "End time must be after start time";
        } else if (duration > 3 && entryType !== 'Workshop') {
          newErrors.endTime = "A single time log cannot exceed 3 hours";
        } else if (duration > 23) {
          newErrors.endTime = "Duration cannot exceed 23 hours";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateDateRange = () => {
    const dates = [];
    const start = moment(startDate);
    const end = moment(endDate);
    
    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      // Skip weekends if option is selected
      if (!skipWeekends || (current.day() !== 0 && current.day() !== 6)) {
        dates.push(current.clone());
      }
      current.add(1, 'day');
    }
    
    return dates;
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    if (!validateForm()) return;

    const dates = generateDateRange();
    if (dates.length === 0) {
      setErrors({ general: 'No valid dates found in the selected range' });
      return;
    }

    // Show confirmation with date count
    setShowConfirmModal(true);
  };

  const handleConfirmCreate = async () => {
    setShowConfirmModal(false);
    setIsCreating(true);
    
    const dates = generateDateRange();
    setProgress({ current: 0, total: dates.length });

    try {
      // Calculate duration from start and end times
      let duration;
      try {
        const startHours = parseHHMMToDecimalHours(startTime);
        const endHours = parseHHMMToDecimalHours(endTime);
        duration = endHours - startHours;
        
        if (duration <= 0) {
          setErrors({ endTime: 'End time must be after start time' });
          setIsCreating(false);
          return;
        }
      } catch (e) {
        setErrors({ general: 'Invalid time format' });
        setIsCreating(false);
        return;
      }

      // Generate unique series ID for tracking
      const seriesId = `series-${Date.now()}`;

      const eventsToCreate = dates.map(date => {
        // Use the specific start and end times from the form
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);

        const eventStart = moment(date).hours(startHour).minutes(startMinute);
        const eventEnd = moment(date).hours(endHour).minutes(endMinute);

        return {
          title: notes,
          start: eventStart.toDate(),
          end: eventEnd.toDate(),
          projectId,
          type: entryType,
          projectName: projectOptions.find(p => p.value === projectId)?.label || '',
          timecodeName: '',
          seriesId: seriesId,
        };
      });

      // Call the parent function to create the series
      await onCreateSeries(eventsToCreate, setProgress);
      
      // Close modal on success
      onClose();
      resetForm();
    } catch (error) {
      setErrors({ general: 'Failed to create series. Please try again.' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEnhanceNotes = async () => {
    if (!notes.trim() || isEnhancingNotes) return;
    setIsEnhancingNotes(true);
    try {
      const projectName = projects.find(p => p.id === projectId)?.name || '';
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/enhance-notes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes, projectName, entryType }),
      });
      const data = await res.json();
      if (data.success) setNotes(data.enhanced.substring(0, 255));
    } catch (e) {
      // silently fail
    } finally {
      setIsEnhancingNotes(false);
    }
  };

  const handleClose = () => {
    if (isCreating) return; // Don't allow closing during creation
    
    if (hasChanges) {
      setShowDiscardModal(true);
    } else {
      onClose();
      resetForm();
    }
  };

  const handleConfirmClose = () => {
    setShowDiscardModal(false);
    onClose();
    resetForm();
  };

  const handleCancelClose = () => {
    setShowDiscardModal(false);
  };

  if (!isOpen) return null;

  const projectOptions = projects.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const dateCount = generateDateRange().length;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2500] p-4"
        onClick={handleClose}
      >
        <div 
          className="bg-[#0D1A27] text-white rounded-lg border border-[#273C61] w-full max-w-md max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-[#0D1A27] border-b border-[#273C61] px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Create Series</h2>
            <button
              onClick={handleClose}
              disabled={isCreating}
              className="text-white/50 hover:text-white disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          <div className="px-6 py-4 space-y-4">
            {errors.general && (
              <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded p-3">
                {errors.general}
              </div>
            )}

            {isCreating && (
              <div className="bg-[#111e2e] border border-[#273C61] rounded p-3">
                <div className="text-sm text-white/70 mb-2">
                  Creating entries... {progress.current} of {progress.total}
                </div>
                <div className="w-full bg-[#273C61] rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(progress.current / progress.total) * 100}%`,
                      backgroundColor: '#FF7800' 
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isCreating}
                  className="w-full p-2 border border-[#273C61] rounded focus:ring-2 focus:ring-[#FF7800] focus:border-[#FF7800] bg-[#111e2e] text-white disabled:opacity-50"
                />
                {hasSubmitted && errors.startDate && (
                  <div className="text-red-500 text-sm mt-1">{errors.startDate}</div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isCreating}
                  className="w-full p-2 border border-[#273C61] rounded focus:ring-2 focus:ring-[#FF7800] focus:border-[#FF7800] bg-[#111e2e] text-white disabled:opacity-50"
                />
                {hasSubmitted && errors.endDate && (
                  <div className="text-red-500 text-sm mt-1">{errors.endDate}</div>
                )}
              </div>
            </div>

                         {/* Time Range */}
             <div className="grid grid-cols-2 gap-4">
               <div>
                 <label className="block text-sm font-medium text-white/80 mb-1">Start Time</label>
                 <input
                   type="time"
                   value={startTime}
                   onChange={(e) => setStartTime(e.target.value)}
                   disabled={isCreating}
                   className="w-full p-2 border border-[#273C61] rounded focus:ring-2 focus:ring-[#FF7800] focus:border-[#FF7800] bg-[#111e2e] text-white disabled:opacity-50"
                 />
                 {hasSubmitted && errors.startTime && (
                   <div className="text-red-500 text-sm mt-1">{errors.startTime}</div>
                 )}
               </div>
               
               <div>
                 <label className="block text-sm font-medium text-white/80 mb-1">End Time</label>
                 <input
                   type="time"
                   value={endTime}
                   onChange={(e) => setEndTime(e.target.value)}
                   disabled={isCreating}
                   className="w-full p-2 border border-[#273C61] rounded focus:ring-2 focus:ring-[#FF7800] focus:border-[#FF7800] bg-[#111e2e] text-white disabled:opacity-50"
                 />
                 {hasSubmitted && errors.endTime && (
                   <div className="text-red-500 text-sm mt-1">{errors.endTime}</div>
                 )}
               </div>
             </div>
             
            {/* Skip Weekends */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={skipWeekends}
                  onChange={(e) => setSkipWeekends(e.target.checked)}
                  disabled={isCreating}
                  className="mr-2 h-4 w-4 accent-[#FF7800] border-[#273C61] rounded disabled:opacity-50"
                />
                <span className="text-sm text-white/80">Skip weekends</span>
              </label>
            </div>

            {/* Date Count Preview */}
            {startDate && endDate && (() => {
              const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
              const hoursPerDay = (timePattern.test(startTime) && timePattern.test(endTime))
                ? Math.max(0, parseHHMMToDecimalHours(endTime) - parseHHMMToDecimalHours(startTime))
                : 0;
              const totalHours = dateCount * hoursPerDay;
              const overLimit = totalHours > 168;
              return (
                <div className={`text-sm rounded p-3 ${overLimit ? 'bg-red-900/30 border border-red-500 text-red-400' : 'bg-[#111e2e] border border-[#273C61] text-white/70'}`}>
                  📅 Will create <strong>{dateCount}</strong> registration{dateCount !== 1 ? 's' : ''}
                  {hoursPerDay > 0 && (
                    <> — <strong>{totalHours % 1 === 0 ? totalHours : totalHours.toFixed(1)}h</strong> total
                    {overLimit && <span className="ml-1 font-semibold">(exceeds 168h limit)</span>}
                    </>
                  )}
                </div>
              );
            })()}

            {/* Matter / Project */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">Project</label>
              <Select
                options={projectOptions}
                value={projectOptions.find(option => option.value === projectId)}
                onChange={(selectedOption) => {
                  if (isProjectBudgetLocked(projects.find(p => p.id === selectedOption?.value))) {
                    setProjectId('');
                    setShowBudgetLocked(true);
                    return;
                  }
                  setProjectId(selectedOption?.value || '');
                }}
                isClearable
                isDisabled={isCreating}
                placeholder="Search or select a project..."
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: '40px',
                    fontSize: '16px',
                    backgroundColor: state.isDisabled ? '#0a1520' : '#111e2e',
                    borderColor: '#273C61',
                    '&:hover': { borderColor: '#205799' },
                  }),
                  placeholder: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.3)' }),
                  input: (provided) => ({ ...provided, color: 'white' }),
                  singleValue: (provided) => ({ ...provided, color: 'white' }),
                  menu: (provided) => ({ ...provided, backgroundColor: '#111e2e', border: '1px solid #273C61' }),
                  menuList: (provided) => ({ ...provided, backgroundColor: '#111e2e' }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isFocused ? '#273C61' : state.isSelected ? '#205799' : '#111e2e',
                    color: 'white',
                  }),
                  indicatorSeparator: (provided) => ({ ...provided, backgroundColor: '#273C61' }),
                  dropdownIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                  clearIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                }}
              />
              {hasSubmitted && errors.projectId && (
                <div className="text-red-500 text-sm mt-1">{errors.projectId}</div>
              )}
            </div>

            {/* Type */}
            <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Type</label>
                <Select
                  options={zrmTypeOptions}
                  value={zrmTypeOptions.find(o => o.value === entryType) || null}
                  onChange={(opt) => setEntryType(opt?.value || '')}
                  isClearable
                  isDisabled={isCreating}
                  placeholder="Select type..."
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: '40px',
                      fontSize: '16px',
                      backgroundColor: state.isDisabled ? '#0a1520' : '#111e2e',
                      borderColor: '#273C61',
                      '&:hover': { borderColor: '#205799' },
                    }),
                    placeholder: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.3)' }),
                    input: (provided) => ({ ...provided, color: 'white' }),
                    singleValue: (provided) => ({ ...provided, color: 'white' }),
                    menu: (provided) => ({ ...provided, backgroundColor: '#111e2e', border: '1px solid #273C61' }),
                    menuList: (provided) => ({ ...provided, backgroundColor: '#111e2e' }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? '#273C61' : state.isSelected ? '#205799' : '#111e2e',
                      color: 'white',
                    }),
                    indicatorSeparator: (provided) => ({ ...provided, backgroundColor: '#273C61' }),
                    dropdownIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                    clearIndicator: (provided) => ({ ...provided, color: 'rgba(255,255,255,0.5)' }),
                  }}
                />
                {hasSubmitted && errors.entryType && (
                  <div className="text-red-500 text-sm mt-1">{errors.entryType}</div>
                )}
            </div>

            {/* Notes */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-white/80">Notes</label>
                <button
                  type="button"
                  onClick={handleEnhanceNotes}
                  disabled={isEnhancingNotes || !notes.trim() || isCreating}
                  className="text-xs text-white/60 hover:text-white disabled:opacity-40"
                  title="Enhance notes with AI"
                >
                  {isEnhancingNotes ? '...' : '✨ Enhance'}
                </button>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isCreating}
                rows={3}
                className="w-full p-2 border border-[#273C61] rounded focus:ring-2 focus:ring-[#FF7800] focus:border-[#FF7800] bg-[#111e2e] text-white disabled:opacity-50"
                placeholder="Enter notes for all entries..."
                maxLength={255}
              />
              <div className="text-sm text-white/50 text-right mt-0.5">{notes.length}/255</div>
              {hasSubmitted && errors.notes && (
                <div className="text-red-500 text-sm mt-1">{errors.notes}</div>
              )}
            </div>

          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-[#0D1A27] border-t border-[#273C61] px-6 py-4 flex justify-between">
            <button
              onClick={handleClose}
              disabled={isCreating}
              className="px-4 py-2 text-white/70 border border-[#273C61] rounded hover:bg-[#273C61] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isCreating}
              className="px-6 py-2 bg-[#FF7800] text-white rounded hover:bg-[#e06800] disabled:opacity-50"
            >
              {isCreating ? 'Creating...' : 'Create Series'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmCreate}
        onCancel={() => setShowConfirmModal(false)}
        title="Create Series Confirmation"
        message={`Are you sure you want to create ${dateCount} time registration${dateCount !== 1 ? 's' : ''} from ${startDate} to ${endDate}?${skipWeekends ? ' (Weekends will be skipped)' : ''}`}
        confirmText="Create All"
        confirmButtonClass="bg-[#0D1A27] hover:bg-[#273C61]"
      />
      
      {/* Discard Changes Confirmation Modal */}
      <ConfirmModal
        isOpen={showDiscardModal}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ErrorModal
        isOpen={showBudgetLocked}
        onClose={() => setShowBudgetLocked(false)}
        title="Budget opbrugt"
        message={BUDGET_LOCKED_MESSAGE}
      />
    </>
  );
}